import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Landmark, FileBadge, Compass, ClipboardList, ShieldCheck,
  Check, ArrowRight, CalendarCheck, Users, Rocket, Target, Minus, Plus,
  Loader2, CheckCircle2, ScrollText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1648747067032-d4300a719cfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const services = [
  { icon: Building2, title: "LLC Formation", desc: "Help organizing a limited liability company and preparing for the next steps." },
  { icon: Landmark, title: "Corporation Formation", desc: "Support for entrepreneurs considering C corporations or S corporation tax treatment." },
  { icon: FileBadge, title: "EIN Application Assistance", desc: "Assistance obtaining a federal Employer Identification Number." },
  { icon: Compass, title: "Business Name & Structure Guidance", desc: "Help evaluating basic entity options based on your goals and business activities." },
  { icon: ClipboardList, title: "State Registration Support", desc: "Guidance through business registration requirements and filing preparation." },
  { icon: ShieldCheck, title: "Ongoing Tax & Compliance Setup", desc: "Help preparing for bookkeeping, tax filings, payroll, sales tax, and annual compliance needs." },
];

const benefits = [
  "Personalized guidance based on your business goals",
  "Clear next steps without confusing jargon",
  "Support beyond formation, including tax planning and compliance",
  "Convenient service for new and growing small businesses",
  "A practical approach focused on helping owners stay organized",
];

const steps = [
  { icon: CalendarCheck, title: "Book a Consultation", desc: "Start with a conversation about where you are and where you want to go." },
  { icon: Target, title: "Review Your Business Goals", desc: "We look at your plans, activities, and priorities together." },
  { icon: ClipboardList, title: "Select & Prepare Your Entity Setup", desc: "Choose the right structure and prepare the paperwork." },
  { icon: Rocket, title: "Launch With a Tax & Compliance Plan", desc: "Start strong with bookkeeping, tax, and compliance ready to go." },
];

const whoWeHelp = [
  "Entrepreneurs", "Freelancers", "Consultants", "Independent contractors",
  "Online sellers", "Service businesses", "Local businesses",
  "Real estate investors", "Side-business starters",
];

const faqs = [
  { q: "Should I form an LLC or a corporation?", a: "It depends on your goals, income, ownership plans, and how you want to be taxed. We help you weigh the practical tax and administrative trade-offs so you can make an informed choice." },
  { q: "Do I need an EIN for my business?", a: "Most businesses need a federal Employer Identification Number to open a bank account, hire employees, or file certain taxes. We can assist you in obtaining one." },
  { q: "Can you help me set up an S corporation?", a: "Yes. We can help entrepreneurs prepare for S corporation tax treatment and understand the requirements and ongoing obligations involved." },
  { q: "How long does entity formation take?", a: "Timelines vary by state and entity type — some filings process in days, others take longer. We'll give you a realistic estimate for your situation." },
  { q: "What do I need before starting my business?", a: "Typically a clear business idea, a chosen name, the state you'll operate in, and an understanding of your ownership and tax goals. We'll walk you through the essentials." },
  { q: "Do you provide legal advice?", a: "No. We provide tax and business-formation support and are not a law firm. For legal questions, please consult a licensed attorney." },
  { q: "Can you help after my business is formed?", a: "Absolutely. We support you beyond formation with bookkeeping, tax planning, payroll, sales tax, and ongoing compliance." },
];

const openBooking = () => window.dispatchEvent(new CustomEvent("open-booking", { detail: { type: "Business Tax Consultation" } }));

export default function EntityFormation() {
  const [openFaq, setOpenFaq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_name: "", business_type: "",
    state: "", entity_type: "", goals: "", preferred_time: "",
  });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    document.title = "Entity Formation Services | LLC, EIN & Small Business Setup";
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "Get practical help starting your business with LLC formation, EIN application assistance, entity selection guidance, and tax and compliance setup.");
    return () => { if (meta && prev) meta.setAttribute("content", prev); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide at least your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        form_type: "entity-formation",
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_category: "Entity Formation",
        source_page: "/entity-formation",
        payload: {
          business_name: form.business_name,
          business_type: form.business_type,
          state: form.state,
          entity_type: form.entity_type,
          goals: form.goals,
          preferred_time: form.preferred_time,
        },
      });
      setDone(true);
      toast.success("Thanks! We'll reach out to discuss your entity-formation needs.");
    } catch {
      toast.error("Something went wrong. Please try again or call 201-253-7674.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="entity-formation-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">Entity Formation Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Start Your Business With the Right Foundation
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              Get guidance choosing and setting up the right business entity, so you can focus on building your business with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={openBooking} className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-semibold" data-testid="entity-hero-book">
                Schedule a Consultation
              </Button>
              <Button onClick={() => document.getElementById("entity-form")?.scrollIntoView({ behavior: "smooth" })} variant="outline" className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium bg-transparent" data-testid="entity-hero-start">
                Get Started
              </Button>
            </div>
            <p className="font-body text-sm text-gray-400 mt-6">
              Practical entity-formation support for entrepreneurs, freelancers, and growing small businesses.
            </p>
          </div>
          <div className="hidden lg:block">
            <img src={HERO_IMG} alt="Entrepreneur planning a new business" className="rounded-2xl shadow-2xl w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">What We Offer</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">Entity Formation Services</h2>
            <p className="font-body text-gray-600 max-w-2xl mx-auto">Business formation services and small business startup support, tailored to your goals.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Card key={s.title} className="service-card h-full hover-lift bg-white" data-testid={`entity-service-${i}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center mb-6">
                    <s.icon className="w-7 h-7 text-bronze" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">{s.title}</h3>
                  <p className="font-body text-gray-600">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Disclaimer */}
          <div className="max-w-4xl mx-auto mt-12 bg-white border border-gray-200 rounded-2xl p-6 flex items-start gap-4" data-testid="entity-disclaimer">
            <ScrollText className="w-6 h-6 text-navy flex-shrink-0 mt-0.5" />
            <p className="font-body text-sm text-gray-600">
              We provide tax and business-formation support and are not a law firm. We do not provide legal advice. For legal questions, please consult a licensed attorney.
            </p>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Why Choose Us</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-8">Build Your Business the Smart Way</h2>
            <ul className="space-y-4">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="font-body text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-bone rounded-2xl p-8 lg:p-10">
            <h3 className="font-heading text-2xl font-bold text-navy mb-4">Not sure where to start?</h3>
            <p className="font-body text-gray-600 mb-6">Book a consultation and we'll help you choose the right structure and map out your next steps.</p>
            <Button onClick={openBooking} className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 font-medium" data-testid="entity-why-book">
              Schedule a Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Simple Process</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <div key={s.title} className="relative" data-testid={`entity-step-${i}`}>
                <div className="bg-white rounded-2xl p-8 h-full shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono text-3xl font-bold text-bronze/40">{`0${i + 1}`}</span>
                    <div className="w-12 h-12 rounded-xl bg-bone flex items-center justify-center">
                      <s.icon className="w-6 h-6 text-bronze" />
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">{s.title}</h3>
                  <p className="font-body text-sm text-gray-600">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Users className="w-5 h-5 text-bronze" />
            <p className="font-mono text-bronze text-sm tracking-wider uppercase">Who We Help</p>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-8">Support for Every Kind of Founder</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {whoWeHelp.map((w) => (
              <span key={w} className="bg-bone text-navy font-body text-sm font-medium px-4 py-2 rounded-full">{w}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">FAQ</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden" data-testid={`entity-faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left font-body font-semibold text-navy"
                  data-testid={`entity-faq-trigger-${i}`}
                >
                  {f.q}
                  {openFaq === i ? <Minus className="w-5 h-5 text-bronze flex-shrink-0" /> : <Plus className="w-5 h-5 text-bronze flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 font-body text-gray-600">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead capture */}
      <section id="entity-form" className="bg-white py-20 md:py-28 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-3">Ready to Start Your Business?</h2>
            <p className="font-body text-gray-600">Take the first step toward building a properly structured business. Schedule a consultation to discuss your entity-formation needs.</p>
          </div>
          {done ? (
            <Card className="bg-bone border-0" data-testid="entity-success">
              <CardContent className="p-10 text-center">
                <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-navy mb-2">Thanks, {form.name.split(" ")[0]}!</h3>
                <p className="font-body text-gray-600 mb-6">We've received your details and will reach out to discuss your entity-formation needs. A confirmation email is on its way.</p>
                <Button onClick={openBooking} className="bg-navy text-white rounded-full px-8">Book a Consultation</Button>
              </CardContent>
            </Card>
          ) : (
          <form onSubmit={submit} className="bg-bone rounded-2xl p-6 md:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ef-name">Full Name *</Label><Input id="ef-name" data-testid="entity-name" value={form.name} onChange={(e) => up("name", e.target.value)} /></div>
              <div><Label htmlFor="ef-email">Email *</Label><Input id="ef-email" type="email" data-testid="entity-email" value={form.email} onChange={(e) => up("email", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ef-phone">Phone</Label><Input id="ef-phone" data-testid="entity-phone" value={form.phone} onChange={(e) => up("phone", e.target.value)} /></div>
              <div><Label htmlFor="ef-biz">Business Name (if applicable)</Label><Input id="ef-biz" data-testid="entity-business" value={form.business_name} onChange={(e) => up("business_name", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ef-type">Type of Business</Label><Input id="ef-type" data-testid="entity-type" value={form.business_type} onChange={(e) => up("business_type", e.target.value)} /></div>
              <div><Label htmlFor="ef-state">State of Operation</Label><Input id="ef-state" data-testid="entity-state" value={form.state} onChange={(e) => up("state", e.target.value)} placeholder="e.g. New York" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ef-entity">Desired Entity Type</Label><Input id="ef-entity" data-testid="entity-desired" value={form.entity_type} onChange={(e) => up("entity_type", e.target.value)} placeholder="LLC, S-Corp, C-Corp, Not sure" /></div>
              <div><Label htmlFor="ef-time">Preferred Consultation Time</Label><Input id="ef-time" data-testid="entity-time" value={form.preferred_time} onChange={(e) => up("preferred_time", e.target.value)} placeholder="e.g. Weekday afternoons" /></div>
            </div>
            <div><Label htmlFor="ef-goals">Brief Description of Business Goals</Label><Textarea id="ef-goals" data-testid="entity-goals" rows={3} value={form.goals} onChange={(e) => up("goals", e.target.value)} /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 text-lg font-medium" data-testid="entity-submit">
              {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</> : <>Request a Consultation <ArrowRight className="ml-2 w-5 h-5" /></>}
            </Button>
            <p className="text-xs text-gray-500 text-center">Your information will be used only to respond to your inquiry and discuss our services.</p>
          </form>
          )}
        </div>
      </section>
    </div>
  );
}
